import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Badge from '../Badge/Badge';
import { useNotificationCount } from '../../../hooks/useNotificationCount';
import { notificationsService } from '../../../services';

export interface NotificationBellProps {
  isActive?: boolean;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ isActive = false }) => {
  const { count, clearCount, refreshCount } = useNotificationCount();
  const location = useLocation();
  const previousPathRef = useRef<string>('');

  // Clear notifications when navigating away from Applications page
  useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = previousPathRef.current;
    
    // If we're navigating away from Applications page, mark notifications as read on backend
    if (previousPath === '/applications' && currentPath !== '/applications') {
      const markNotificationsAsRead = async () => {
        try {
          console.log('🔔 NotificationBell: Marking all notifications as read on backend');
          
          // Call the mark-all-read endpoint
          const success = await notificationsService.markAllAsRead();
          
          if (success) {
            console.log('✅ NotificationBell: All notifications marked as read on backend');
            
            // Clear the local count after successful backend operation
            clearCount();
            
            // Refresh count from API to confirm
            refreshCount();
          } else {
            console.warn('⚠️ NotificationBell: Backend mark-all-read failed, clearing locally only');
            clearCount();
          }
          
        } catch (error) {
          console.error('❌ NotificationBell: Error marking notifications as read:', error);
          // Still clear local count even if backend fails
          clearCount();
        }
      };
      
      markNotificationsAsRead();
    }
    
    // Update the previous path
    previousPathRef.current = currentPath;
  }, [location.pathname, clearCount, refreshCount]);

  // Listen for external updates and refresh from API
  useEffect(() => {
    const handleExternalUpdate = () => {
      // When external components signal an update, refresh from API
      refreshCount();
      console.log('🔔 NotificationBell: Refreshing count from API due to external update');
    };

    // Listen for custom events from notificationAPI
    window.addEventListener('notificationUpdate', handleExternalUpdate);
    return () => window.removeEventListener('notificationUpdate', handleExternalUpdate);
  }, [refreshCount]);

  return (
    <Badge 
      count={count}
      isActive={false}
      maxCount={99}
      className="notification-bell"
    />
  );
};

export default NotificationBell;